import type { Document } from "@/types/documents";

/**
 * Continuous zoom level - 0 (most zoomed out) to 100 (most zoomed in)
 * This enables smooth scrolling/pinching instead of discrete jumps
 */
export type ZoomLevel = number;

/** Zoom configuration constants */
export const ZOOM_CONFIG = {
  /** Minimum zoom level (most zoomed out - year view) */
  MIN: 0,
  /** Maximum zoom level (most zoomed in - minute view) */
  MAX: 100,
  /** Default starting zoom level */
  DEFAULT: 30,
  /** Increment for button clicks */
  STEP: 5,
  /** Increment for smooth wheel scrolling */
  WHEEL_STEP: 2,
} as const;

/**
 * Get minimum card gap (clustering threshold) based on zoom level
 * IMPORTANT: Threshold > base spacing so items with 0 time gap cluster
 * Time bonus pushes different-time items beyond threshold → no cluster
 */
export function getMinCardGap(zoomLevel: number): number {
  // At low zoom (0): 40px threshold > 30px base spacing
  // At high zoom (100): 70px threshold > 50px base spacing
  // Items with same timestamp: spacing = base only → cluster
  // Items with different time: spacing = base + bonus → exceeds threshold → no cluster
  const minGap = 40;
  const maxGap = 70;
  return minGap + (zoomLevel / 100) * (maxGap - minGap);
}

/**
 * A single item on the timeline (represents one document)
 */
export interface TimelineItem {
  id: string;
  document: Document;
  timestamp: Date;
}

/**
 * A cluster of timeline items that are grouped together when zoomed out
 */
export interface TimelineCluster {
  id: string;
  type: "single" | "cluster";
  items: TimelineItem[];
  startTime: Date;
  endTime: Date;
  /** Normalized position (0-1) on the timeline */
  normalizedPosition: number;
  /** Pixel position from top */
  pixelPosition: number;
  /** Which side of the stem (alternates for visual balance) */
  side: "left" | "right";
}

/**
 * The computed timeline data after clustering
 */
export interface TimelineData {
  clusters: TimelineCluster[];
  timeRange: {
    start: Date;
    end: Date;
    durationMs: number;
  };
  totalDocuments: number;
}

/**
 * Timeline view state
 */
export interface TimelineViewState {
  zoomLevel: ZoomLevel;
  expandedClusterIds: Set<string>;
  selectedDocumentIds: Set<string>;
  /** Vertical scroll position */
  scrollTop: number;
  /** Optional time range filter */
  visibleTimeRange?: {
    start: Date;
    end: Date;
  };
}

/**
 * Date marker on the timeline stem
 */
export interface TimelineDateMarker {
  date: Date;
  label: string;
  normalizedPosition: number;
  pixelPosition: number;
  isMinor: boolean;
}

/**
 * Get the effective timestamp for a document (prefers original_created_at)
 */
export function getDocumentTimestamp(doc: Document): Date {
  const dateStr = doc.original_created_at || doc.created_at;
  return new Date(dateStr);
}

/**
 * Check if a zoom level allows clustering
 * At very high zoom (>90), no clustering - show everything individually
 */
export function canCluster(zoomLevel: ZoomLevel): boolean {
  return zoomLevel < 90;
}
